/* global Blob, setTimeout */
import { pbBaseUrl } from './pbBaseUrl';

/**
 * Staff dashboard client. The only authenticated calls in the web app.
 *
 * Sign-in reuses the app's own magic-link flow (PocketBase email OTP) — there
 * are no passwords (ADR-0005). The token lives in localStorage so a closed tab
 * does not sign you out, but only for STAFF_IDLE_MS since it was last
 * refreshed (every load re-mints it via authRefresh, so use keeps it alive).
 * The server decides who is staff: `/api/admin/*` answers 403 unless
 * `users.is_admin`.
 */

const TOKEN_KEY = 'skf:staff:token';
const TOKEN_AT_KEY = 'skf:staff:token_at';
/** Idle expiry for the stored token. The server session is longer (90 days);
 *  an admin token sitting in localStorage gets the shorter bound. */
export const STAFF_IDLE_MS = 14 * 24 * 60 * 60 * 1000;

/** A metric with the plain-English rule that produced it. */
export interface Metric {
  value: number | null;
  definition: string;
  window?: string;
  unit?: string;
}
export interface StatusGroup {
  total: number;
  by_status: Record<string, number>;
  definition: string;
}
export interface Windowed {
  last_24h: number;
  last_7d: number;
  last_30d: number;
  definition: string;
}
export interface Point {
  date: string;
  [k: string]: number | string;
}
export interface SeriesBlock {
  points: Point[];
  definition: string;
}
export interface Overview {
  generated_at: string;
  exclusions: string;
  users: Record<
    'total' | 'hourly_active' | 'daily_active' | 'weekly_active' | 'monthly_active' | 'stickiness',
    Metric
  >;
  skiffs: Record<'active' | 'ended' | 'other', StatusGroup>;
  memberships: Record<
    'monthly' | 'annual' | 'lifetime' | 'total' | 'unclassified' | 'pro_without_subscription',
    Metric
  >;
  ai: {
    requests: Windowed;
    llm_calls: Windowed;
    cache_hit_rate: Metric;
    cost_usd: Windowed;
    tokens_30d: { input: number; output: number; definition: string };
    by_prompt_mode_30d: {
      rows: { prompt_mode: string; requests: number; llm_calls: number; cost_usd: number }[];
      definition: string;
    };
    by_model_30d: {
      rows: { provider: string; model: string; llm_calls: number; cost_usd: number }[];
      definition: string;
    };
  };
  series: {
    days: number;
    bucket: string;
    signups: SeriesBlock;
    total_users: SeriesBlock;
    daily_active: SeriesBlock;
    skiffs_created: SeriesBlock;
    skiffs_by_status: SeriesBlock;
    memberships: SeriesBlock;
    ai: SeriesBlock;
  };
}

export class StaffError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

function store(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_AT_KEY, String(Date.now()));
  } catch {
    /* storage unavailable — session lasts for this load only */
  }
}

export function getToken(): string | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    const at = Number(localStorage.getItem(TOKEN_AT_KEY) ?? 0);
    // A token with no timestamp is treated as just-issued rather than
    // discarded, so it is bounded from its first refresh onward.
    if (at && Date.now() - at > STAFF_IDLE_MS) {
      clearToken();
      return null;
    }
    return token;
  } catch {
    return null;
  }
}
export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_AT_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

/** Re-mint the token (slides both the server expiry and our idle window).
 *  Only an explicit 401 signs out; a network failure keeps the session. */
export async function refreshSession(): Promise<boolean> {
  const token = getToken();
  if (!token) return false;
  try {
    const res = await fetch(`${pbBaseUrl()}/api/collections/users/auth-refresh`, {
      method: 'POST',
      headers: { Authorization: token },
    });
    if (res.status === 401) {
      clearToken();
      return false;
    }
    if (res.ok) store(((await res.json()) as { token: string }).token);
  } catch {
    /* offline — keep the stored session */
  }
  return true;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${pbBaseUrl()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok)
    throw new StaffError(
      res.status,
      res.status === 429 ? 'Too many attempts — wait a minute.' : 'Request failed.',
    );
  return (await res.json()) as T;
}

/** Step 1: email a one-time code. Returns the otpId to pair with it. */
export async function requestCode(email: string): Promise<string> {
  const r = await post<{ otpId: string }>('/api/collections/users/request-otp', { email });
  return r.otpId;
}

/** Step 2: exchange the code for a session token. */
export async function verifyCode(otpId: string, code: string): Promise<void> {
  const r = await post<{ token: string }>('/api/collections/users/auth-with-otp', {
    otpId,
    password: code,
  });
  store(r.token);
}

export async function fetchOverview(days = 30): Promise<Overview> {
  const token = getToken();
  if (!token) throw new StaffError(401, 'Not signed in.');
  const res = await fetch(`${pbBaseUrl()}/api/admin/overview?days=${days}`, {
    headers: { Authorization: token },
  });
  if (res.status === 401) clearToken();
  if (!res.ok) {
    throw new StaffError(
      res.status,
      res.status === 403
        ? 'This account is not staff.'
        : res.status === 401
          ? 'Session expired.'
          : 'Could not load the dashboard.',
    );
  }
  return (await res.json()) as Overview;
}

async function authed<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const token = getToken();
  if (!token) throw new StaffError(401, 'Not signed in.');
  const res = await fetch(`${pbBaseUrl()}${path}`, {
    method: init.method ?? 'GET',
    headers: {
      Authorization: token,
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  if (res.status === 401) clearToken();
  if (!res.ok) {
    let msg = res.status === 403 ? 'This account is not staff.' : 'Request failed.';
    try {
      const j = (await res.json()) as { error?: string; message?: string };
      if (res.status !== 403 && (j.error || j.message)) msg = (j.error || j.message) as string;
    } catch {
      /* non-JSON error body */
    }
    throw new StaffError(res.status, msg);
  }
  return (await res.json()) as T;
}

export interface Attachment {
  kind: 'image' | 'video' | 'gif';
  url: string;
}

export interface Report {
  id: string;
  created: string;
  status: 'pending' | 'resolved' | 'dismissed';
  reason: string;
  target_type: string;
  target_id: string;
  skiff_id: string;
  skiff_title: string;
  reporter_id: string;
  reported_user_id: string;
  reporter: string;
  reported_user: string;
  escalated_to_admin: boolean;
  content_snippet: string | null;
  attachments: Attachment[];
  evidence_id: string;
  content_removed: boolean;
  resolved_at: string;
}
export interface PendingDeletion {
  id: string;
  display_name: string;
  email: string;
  requested_at: string;
  days_remaining: number | null;
  due: boolean;
}

export const fetchReports = (status: string) =>
  authed<{ reports: Report[] }>(`/api/admin/reports?status=${encodeURIComponent(status)}`);
export const resolveReport = (
  reportId: string,
  resolution: 'resolved' | 'dismissed',
  removeContent: boolean,
  note: string,
) =>
  authed<{ ok: boolean }>('/api/admin/reports/resolve', {
    method: 'POST',
    body: { reportId, resolution, removeContent, note },
  });
export const fetchDeletions = () =>
  authed<{ deletions: PendingDeletion[]; grace_period_days: number }>('/api/admin/deletions');
export const requestDeletion = (email: string) =>
  authed<{ ok: boolean; id: string }>('/api/admin/deletions/request', {
    method: 'POST',
    body: { email },
  });
export const cancelDeletion = (userId: string) =>
  authed<{ ok: boolean }>('/api/admin/deletions/cancel', { method: 'POST', body: { userId } });
export const deleteNow = (userId: string) =>
  authed<{ ok: boolean; vendor_pending: boolean }>('/api/admin/deletions/delete-now', {
    method: 'POST',
    body: { userId, confirm: 'DELETE' },
  });

export interface AuditReportRow {
  id: string;
  created: string;
  status: string;
  reason: string;
  target_type: string;
  skiff_title: string;
  reporter: string;
  reported_user: string;
  content_snippet: string | null;
  attachments: Attachment[];
}
export interface UserAudit {
  user: {
    id: string;
    display_name: string;
    email: string;
    created: string;
    account_status: string;
    is_admin: boolean;
  };
  memberships: Array<{
    skiff_id: string;
    skiff_title: string;
    role: string;
    status: string;
    joined_at: string;
  }>;
  reports_against: AuditReportRow[];
  reports_filed: AuditReportRow[];
  filing_stats: {
    total: number;
    pending: number;
    resolved: number;
    dismissed: number;
    last_30d: number;
    dismissal_rate: number | null;
    top_reported: Array<{ name: string; count: number }>;
  };
  recent_messages: Array<{
    id: string;
    created: string;
    skiff_title: string;
    content: string;
    removed: boolean;
    attachments: Attachment[];
  }>;
  recent_checkins: Array<{
    id: string;
    created: string;
    skiff_title: string;
    note: string;
    attachments: Attachment[];
  }>;
  moderation_history: Array<{ created: string; action: string; actor: string; reason: string }>;
}
export type UserActionName =
  'warn' | 'ban' | 'unban' | 'remove_from_skiff' | 'dismiss_reporter_reports' | 'delete_account';

export const fetchUserAudit = (userId: string) =>
  authed<UserAudit>(`/api/admin/user-audit?userId=${encodeURIComponent(userId)}`);
export const userAction = (
  userId: string,
  action: UserActionName,
  reason: string,
  skiffId?: string,
) =>
  authed<{ ok: boolean }>('/api/admin/user-action', {
    method: 'POST',
    body: { userId, action, reason, skiffId },
  });

export interface EvidencePackage {
  id: string;
  created: string;
  preserved_by: string;
  snapshot: unknown;
  files: Array<{ name: string; key: string; size: number; content_type: string; md5_hex: string }>;
}
export const preserveEvidence = (reportId: string) =>
  authed<{ ok: boolean; evidence_id: string; files: number }>('/api/admin/evidence/preserve', {
    method: 'POST',
    body: { reportId },
  });
export const fetchEvidence = (reportId: string) =>
  authed<EvidencePackage>(`/api/admin/evidence?reportId=${encodeURIComponent(reportId)}`);

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  // Firefox needs the anchor in the DOM; revoking too early cancels the save.
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Downloads the evidence package JSON, then each preserved file. */
export async function exportEvidence(reportId: string): Promise<string[]> {
  const pkg = await fetchEvidence(reportId);
  const saved = [`evidence-${reportId}.json`];
  saveBlob(
    new Blob([JSON.stringify(pkg, null, 2)], { type: 'application/json' }),
    `evidence-${reportId}.json`,
  );
  const token = getToken();
  for (const f of pkg.files) {
    const res = await fetch(
      `${pbBaseUrl()}/api/admin/evidence/file?id=${encodeURIComponent(pkg.id)}&name=${encodeURIComponent(f.name)}`,
      { headers: { Authorization: token ?? '' } },
    );
    if (!res.ok) throw new StaffError(res.status, `Could not download ${f.name}.`);
    saveBlob(await res.blob(), `evidence-${reportId}-${f.name}`);
    saved.push(`evidence-${reportId}-${f.name}`);
  }
  return saved;
}
